import {
  ArrowRight,
  Buildings,
  ChatCenteredText,
  Check,
  CursorClick,
  FileText,
  Globe,
  HandPalm,
  ImageSquare,
  LockKey,
  MagnifyingGlass,
  Megaphone,
  ShieldCheck,
  ShieldWarning,
  Sparkle,
  Timer,
  Trash,
  type Icon,
} from "@phosphor-icons/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useLayoutEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAssessmentSession } from "../features/session/AssessmentSessionContext";

gsap.registerPlugin(ScrollTrigger);

const questSteps: Array<{ number: string; title: string; description: string; meta: string; icon: Icon }> = [
  { number: "01", title: "Business identity", description: "Make the legal name, optional DBA, website, and campaign purpose line up.", meta: "Trust & authenticity", icon: Buildings },
  { number: "02", title: "Real opt-in", description: "Show the consent journey exactly as customers experience it.", meta: "Choice & consent", icon: CursorClick },
  { number: "03", title: "Message behavior", description: "Check content, cadence, opt-out language, and message flow together.", meta: "Use case & content", icon: ChatCenteredText },
  { number: "04", title: "Evidence pack", description: "Organize the proof a reviewer needs into one consistent campaign story.", meta: "Proof that holds up", icon: ImageSquare },
];

const checkGroups: Array<{ index: string; title: string; copy: string; items: [string, Icon][] }> = [
  {
    index: "01",
    title: "Identity",
    copy: "Can a reviewer quickly understand who is sending and why?",
    items: [["Website", Globe], ["Business consistency", Buildings], ["Privacy Policy", ShieldCheck], ["Terms of Service", FileText]],
  },
  {
    index: "02",
    title: "Consent",
    copy: "Does the real customer journey make the choice unmistakable?",
    items: [["Opt-in flow", CursorClick], ["Consent wording", ChatCenteredText], ["Campaign description", Megaphone], ["Message flow", ArrowRight]],
  },
  {
    index: "03",
    title: "Proof",
    copy: "Do the messages and screenshots support the registration story?",
    items: [["Sample messages", ChatCenteredText], ["STOP / HELP", HandPalm], ["Screenshot evidence", ImageSquare], ["Rejection risks", ShieldWarning]],
  },
];

const privacyFeatures: [Icon, string, string][] = [
  [ShieldCheck, "No account", "Start anonymously"],
  [LockKey, "No EIN", "Never requested here"],
  [Trash, "Delete early", "Stay in control"],
];

export function LandingPage() {
  const pageRef = useRef<HTMLElement>(null);
  const heroRef = useRef<HTMLElement>(null);
  const heroArtRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { hasResumableSession, startNew, busy } = useAssessmentSession();

  async function begin() {
    await startNew();
    navigate("/assessment/new");
  }

  useLayoutEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const context = gsap.context(() => {
      gsap.timeline({ defaults: { ease: "power3.out" } })
        .from("[data-hero-copy]", { autoAlpha: 0, y: 28, duration: 0.72, stagger: 0.09 })
        .from(heroArtRef.current, { autoAlpha: 0, scale: 1.035, x: 34, duration: 1.05 }, 0.14)
        .from("[data-quest-callout]", { autoAlpha: 0, y: 16, duration: 0.48, stagger: 0.08 }, 0.62);

      gsap.utils.toArray<HTMLElement>("[data-scroll-reveal]").forEach((element) => {
        gsap.from(element, { autoAlpha: 0, y: 28, duration: 0.7, ease: "power3.out", scrollTrigger: { trigger: element, start: "top 88%", once: true } });
      });

      const hero = heroRef.current;
      const art = heroArtRef.current;
      const caustics = root.querySelector<HTMLElement>(".quest-water-caustics");
      const cards = gsap.utils.toArray<HTMLElement>("[data-quest-callout]");
      if (!hero || !art || !caustics) return;

      const moveArtX = gsap.quickTo(art, "x", { duration: 1.15, ease: "power3.out" });
      const moveArtY = gsap.quickTo(art, "y", { duration: 1.15, ease: "power3.out" });
      const moveLightX = gsap.quickTo(caustics, "x", { duration: 0.8, ease: "power2.out" });
      const moveLightY = gsap.quickTo(caustics, "y", { duration: 0.8, ease: "power2.out" });

      const moveThroughWater = (event: PointerEvent) => {
        if (event.pointerType === "touch") return;
        const bounds = hero.getBoundingClientRect();
        const x = (event.clientX - bounds.left) / bounds.width - 0.5;
        const y = (event.clientY - bounds.top) / bounds.height - 0.5;
        hero.style.setProperty("--water-x", `${50 + x * 30}%`);
        hero.style.setProperty("--water-y", `${50 + y * 30}%`);
        moveArtX(x * -34);
        moveArtY(y * -22);
        moveLightX(x * 54);
        moveLightY(y * 38);
        cards.forEach((card, index) => {
          gsap.to(card, { x: x * (5 + index * 1.6), y: y * (4 + index), duration: 0.85, ease: "power3.out", overwrite: "auto" });
        });
      };

      const settleWater = () => {
        hero.style.setProperty("--water-x", "50%");
        hero.style.setProperty("--water-y", "50%");
        moveArtX(0);
        moveArtY(0);
        moveLightX(0);
        moveLightY(0);
        cards.forEach((card) => gsap.to(card, { x: 0, y: 0, duration: 1.15, ease: "elastic.out(1, .55)", overwrite: "auto" }));
      };

      hero.addEventListener("pointermove", moveThroughWater);
      hero.addEventListener("pointerleave", settleWater);
      return () => {
        hero.removeEventListener("pointermove", moveThroughWater);
        hero.removeEventListener("pointerleave", settleWater);
      };
    }, root);
    return () => context.revert();
  }, []);

  const primaryLabel = hasResumableSession ? "Resume readiness quest" : "Start readiness quest";

  return (
    <main ref={pageRef} className="quest-page">
      <section ref={heroRef} className="quest-hero">
        <div ref={heroArtRef} className="quest-hero-art" aria-hidden="true" />
        <div className="quest-hero-vignette" aria-hidden="true" />
        <div className="quest-water-caustics" aria-hidden="true" />
        <div className="quest-waterline" aria-hidden="true" />
        <div className="quest-hero-inner">
          <div className="quest-hero-copy">
            <p data-hero-copy className="quest-kicker">A2P 10DLC readiness & rejection help</p>
            <h1 data-hero-copy>Catch Issues<br />Before Carriers Do<span>.</span></h1>
            <p data-hero-copy className="quest-lede">Spot the traps, fix the gaps, and reach carrier review with a clearer campaign.</p>
            <div data-hero-copy className="quest-actions">
              <button type="button" className="quest-button quest-button-primary" onClick={() => hasResumableSession ? navigate("/assessment/current") : void begin()} disabled={busy}>
                <Sparkle size={22} weight="fill" /> {primaryLabel} <ArrowRight size={19} weight="bold" />
              </button>
              <button type="button" className="quest-button quest-button-secondary" onClick={() => navigate("/rejection-check")}>
                <MagnifyingGlass size={21} weight="bold" /> Decode a rejection <ArrowRight size={18} weight="bold" />
              </button>
            </div>
            <div data-hero-copy className="quest-trust-row">
              <span><Check size={14} weight="bold" /> Low Volume Mixed</span>
              <span><Check size={14} weight="bold" /> DBA optional</span>
              <span><Check size={14} weight="bold" /> No account</span>
            </div>
          </div>

          <div className="quest-map-callouts" aria-label="Four-stage readiness quest">
            {questSteps.map((step, index) => (
              <div key={step.number} data-quest-callout className={`quest-map-callout callout-${index + 1}`}>
                <strong>{index + 1}</strong>
                <span><b>{step.title}</b><small>{step.description.split(".")[0]}.</small></span>
              </div>
            ))}
          </div>

          <aside data-quest-callout className="quest-review-card liquid-glass">
            <div className="quest-review-radar"><ShieldCheck size={26} weight="duotone" /></div>
            <div><strong>12 reviewer checks</strong><span>Across identity, consent, messages, and proof.</span></div>
          </aside>
        </div>
      </section>

      <section className="quest-signal-strip" aria-label="Assessment details">
        {[["Campaign type", "Low Volume Mixed"], ["Two clear paths", "Ready + declined"], ["Reviewer coverage", "12 focused checks"], ["Data window", "Up to 7 days"]].map(([label, value]) => (
          <div key={label}><span>{label}</span><strong>{value}</strong></div>
        ))}
      </section>

      <section id="how-it-works" className="quest-section quest-process">
        <div className="quest-section-heading" data-scroll-reveal>
          <div><p className="quest-kicker">Your route to review</p><h2>Four checkpoints.<br />One calmer campaign.</h2></div>
          <p>Think of it like clearing a quest map: each stop solves one reviewer question before it becomes a rejection.</p>
        </div>
        <div className="quest-stage-grid">
          {questSteps.map(({ number, title, description, meta, icon: Icon }, index) => (
            <article key={number} data-scroll-reveal className={`quest-stage-card ${index === 1 ? "is-active" : ""}`}>
              <div className="quest-stage-top"><span>{number}</span><i><Icon size={22} weight="duotone" /></i></div>
              <div><p>{meta}</p><h3>{title}</h3><span>{description}</span></div>
              <div className="quest-stage-progress"><span /></div>
            </article>
          ))}
        </div>
        <div className="quest-process-foot" data-scroll-reveal>
          <span>Start with scattered campaign details</span><ArrowRight size={18} /><strong>Leave with a focused correction plan</strong>
        </div>
      </section>

      <section id="what-we-check" className="quest-section quest-coverage">
        <div className="quest-coverage-visual" data-scroll-reveal>
          <div className="quest-character-stage">
            <div className="quest-character-label"><span>Campaign scan</span><strong>Connect the clues</strong></div>
            <div className="quest-character-signals" aria-hidden="true">
              <span><Buildings size={20} weight="duotone" /> Identity</span>
              <span><CursorClick size={20} weight="duotone" /> Consent</span>
              <span><ChatCenteredText size={20} weight="duotone" /> Messages</span>
            </div>
            <img src="/assets/a2bait-shark-guide-transparent.png" alt="Cartoon A2bait shark guide pointing toward campaign review signals" />
            <div className="quest-coverage-badge liquid-glass"><strong>12</strong><span>signals scanned</span></div>
          </div>
        </div>
        <div className="quest-coverage-content">
          <div data-scroll-reveal>
            <p className="quest-kicker">Reviewer coverage</p>
            <h2>See the campaign the way a reviewer connects it.</h2>
            <p className="quest-section-copy">Three systems turn a pile of details into one clear story. Open each panel to see the four checks inside.</p>
          </div>
          <div className="quest-check-stack">
            {checkGroups.map((group, groupIndex) => (
              <details key={group.index} data-scroll-reveal className="quest-check-panel" open={groupIndex === 0}>
                <summary><span>{group.index}</span><div><strong>{group.title}</strong><small>{group.copy}</small></div><i>{group.items.length.toString().padStart(2, "0")}</i></summary>
                <div className="quest-check-items">
                  {group.items.map(([label, Icon]) => <span key={label}><Icon size={17} weight="duotone" /> {label}</span>)}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section id="privacy" className="quest-section quest-privacy-wrap">
        <div className="quest-privacy" data-scroll-reveal>
          <div className="quest-privacy-copy">
            <p className="quest-kicker">Private by default</p>
            <h2>Your evidence isn’t permanent loot.</h2>
            <p>Assessment data and evidence are temporary, no account is required, and sensitive tax identifiers are never requested here.</p>
            <div className="quest-privacy-features">
              {privacyFeatures.map(([Icon, title, detail]) => <div key={title}><Icon size={20} weight="duotone" /><span><strong>{title}</strong><small>{detail}</small></span></div>)}
            </div>
            <div className="quest-retention"><Timer size={22} /><strong>07</strong><span>days maximum<br />retention window</span></div>
          </div>
          <div className="quest-privacy-art"><img src="/assets/a2bait-private-vault.png" alt="Cartoon shark guarding a temporary underwater evidence vault" /></div>
        </div>
      </section>
    </main>
  );
}
